import type { SupportedLocale } from '@erp/shared';

const en = {
  title: 'orgaMAX images and history',
  description:
    'Import prepared images and historical documents after the spreadsheet import. Select the extracted package folder and review the preview before confirming.',
  directory: 'Extracted package folder',
  folderHint:
    'Choose the complete folder containing manifest.json, records.jsonl and files. ZIP files must be extracted first. Originals are checked locally before transfer.',
  targetTitle: 'Import destination',
  environment: 'Environment',
  tenant: 'Tenant',
  origin: 'ERP address',
  confirmTarget: 'I have checked this tenant and environment as the destination.',
  prepare: 'Prepare preview',
  confirm: 'Confirm import',
  resume: 'Resume this import',
  stop: 'Stop transfer',
  retry: 'Retry',
  permissions: 'You need permission to read and manage imports.',
  targetError: 'The destination could not be verified. Reload it before continuing.',
  statusError: 'The run status could not be loaded. Reload the status before continuing.',
  localReady: 'Package verified locally',
  packageSummary: '{{records}} source rows · {{files}} original files',
  sourceSummary: 'Source installation: {{instance}} · Source tenant: {{tenant}}',
  restoreHint:
    'Saved run {{id}} is available. Select the same package folder to continue with its verified preview.',
  dmsPermission: 'You need permission to upload documents to transfer the originals.',
  executePermission: 'You need permission to execute imports before confirming.',
  previewTitle: 'Import preview',
  previewSummary: '{{created}} new · {{skipped}} already present · {{invalid}} blocked',
  previewBlocked:
    'Resolve every blocking issue before confirming. No business records have been imported.',
  previewReady: 'The preview has no blocking issues. Confirm explicitly to start the import.',
  jobProgress: 'Run {{id}} · {{status}} · {{count}} / {{total}} rows',
  deferred:
    'History and originals are preserved. Opening inventory and receivables remain deferred.',
  disconnectHint:
    'Closing this page or stopping interrupts the browser transfer. Existing evidence is retained; select the same folder to continue. A confirmed server import continues independently.',
  environments: {
    development: 'Development',
    test: 'Test',
    staging: 'Staging',
    production: 'Production',
  },
  phases: {
    VERIFY_RECORDS: 'Checking source rows: {{current}} / {{total}}',
    VERIFY_FILES: 'Checking originals: {{current}} / {{total}}',
    UPLOAD_RECORDS: 'Transferring source rows: {{current}} / {{total}}',
    UPLOAD_FILES: 'Transferring originals: {{current}} / {{total}}',
    PREVIEW: 'Preparing the server preview…',
  },
  errors: {
    INVALID_PACKAGE: 'The folder is not a supported, complete package.',
    RECORD_INTEGRITY:
      'Source rows are missing, changed or too large. Select the complete unchanged package.',
    FILE_INTEGRITY: 'An original file is missing, changed or exceeds the permitted size.',
    SOURCE_MISMATCH: 'The source installation, tenant or mapping does not match the package.',
    CANCELLED:
      'Transfer stopped. Select the same package and prepare again to continue the same run.',
    TARGET_CHANGED: 'The destination changed. Reload and verify the destination before continuing.',
    PERSISTENCE_UNAVAILABLE:
      'The browser cannot save the resume proof. Enable local storage and try again.',
    TRANSFER_FAILED:
      'The operation was interrupted. Check your connection and continue with the same package.',
  },
  confirmed: 'Import confirmed.',
  issuesLimited:
    'Showing the first {{shown}} of {{total}} blocking issues. Prepare the preview again if the full report is no longer available.',
  downloadReport: 'Download issue report',
};
export const portableImportTranslations: Record<SupportedLocale, typeof en> = {
  en,
  de: {
    title: 'orgaMAX-Bilder und Historie',
    description:
      'Vorbereitete Bilder und Altbelege nach dem Tabellenimport übernehmen. Wählen Sie den entpackten Paketordner und prüfen Sie die Vorschau vor der Bestätigung.',
    directory: 'Entpackter Paketordner',
    folderHint:
      'Wählen Sie den vollständigen Ordner mit manifest.json, records.jsonl und files. ZIP-Dateien vorher entpacken. Originale werden vor der Übertragung lokal geprüft.',
    targetTitle: 'Übernahmeziel',
    environment: 'Umgebung',
    tenant: 'Mandant',
    origin: 'ERP-Adresse',
    confirmTarget: 'Ich habe diesen Mandanten und diese Umgebung als Ziel geprüft.',
    prepare: 'Vorschau vorbereiten',
    confirm: 'Übernahme bestätigen',
    resume: 'Diese Übernahme fortsetzen',
    stop: 'Übertragung stoppen',
    retry: 'Erneut versuchen',
    permissions: 'Sie benötigen die Rechte zum Lesen und Verwalten von Importen.',
    targetError: 'Das Ziel konnte nicht geprüft werden. Laden Sie es vor dem Fortfahren erneut.',
    statusError:
      'Der Laufstatus konnte nicht geladen werden. Laden Sie ihn vor dem Fortfahren erneut.',
    localReady: 'Paket lokal geprüft',
    packageSummary: '{{records}} Quellzeilen · {{files}} Originaldateien',
    sourceSummary: 'Quellinstallation: {{instance}} · Quellmandant: {{tenant}}',
    restoreHint:
      'Der gespeicherte Lauf {{id}} ist verfügbar. Wählen Sie denselben Paketordner, um mit seiner geprüften Vorschau fortzufahren.',
    dmsPermission: 'Für die Originale benötigen Sie das Recht zum Hochladen von Dokumenten.',
    executePermission: 'Für die Bestätigung benötigen Sie das Recht zum Ausführen von Importen.',
    previewTitle: 'Übernahmevorschau',
    previewSummary: '{{created}} neu · {{skipped}} bereits vorhanden · {{invalid}} gesperrt',
    previewBlocked:
      'Klären Sie alle Sperrgründe vor der Bestätigung. Es wurden noch keine Fachdaten übernommen.',
    previewReady:
      'Die Vorschau enthält keine Sperrgründe. Starten Sie die Übernahme durch ausdrückliche Bestätigung.',
    jobProgress: 'Lauf {{id}} · {{status}} · {{count}} / {{total}} Zeilen',
    deferred:
      'Historie und Originale bleiben erhalten. Anfangsbestand und offene Posten werden weiterhin zurückgestellt.',
    disconnectHint:
      'Schließen der Seite oder Stoppen unterbricht die Browserübertragung. Gespeicherte Nachweise bleiben erhalten; wählen Sie zum Fortsetzen denselben Ordner. Eine bestätigte Übernahme läuft auf dem Server weiter.',
    environments: {
      development: 'Entwicklung',
      test: 'Test',
      staging: 'Staging',
      production: 'Produktion',
    },
    phases: {
      VERIFY_RECORDS: 'Quellzeilen prüfen: {{current}} / {{total}}',
      VERIFY_FILES: 'Originale prüfen: {{current}} / {{total}}',
      UPLOAD_RECORDS: 'Quellzeilen übertragen: {{current}} / {{total}}',
      UPLOAD_FILES: 'Originale übertragen: {{current}} / {{total}}',
      PREVIEW: 'Servervorschau wird vorbereitet…',
    },
    errors: {
      INVALID_PACKAGE: 'Der Ordner enthält kein unterstütztes, vollständiges Paket.',
      RECORD_INTEGRITY:
        'Quellzeilen fehlen, wurden verändert oder sind zu groß. Wählen Sie das vollständige, unveränderte Paket.',
      FILE_INTEGRITY:
        'Eine Originaldatei fehlt, wurde verändert oder überschreitet die erlaubte Größe.',
      SOURCE_MISMATCH: 'Quellinstallation, Mandant oder Zuordnung passen nicht zum Paket.',
      CANCELLED:
        'Übertragung gestoppt. Wählen Sie dasselbe Paket und bereiten Sie es erneut vor, um denselben Lauf fortzusetzen.',
      TARGET_CHANGED:
        'Das Ziel hat sich geändert. Laden und prüfen Sie es vor dem Fortfahren erneut.',
      PERSISTENCE_UNAVAILABLE:
        'Der Browser kann den Fortsetzungsnachweis nicht speichern. Aktivieren Sie den lokalen Speicher und versuchen Sie es erneut.',
      TRANSFER_FAILED:
        'Der Vorgang wurde unterbrochen. Prüfen Sie die Verbindung und setzen Sie mit demselben Paket fort.',
    },
    confirmed: 'Übernahme bestätigt.',
    issuesLimited:
      'Die ersten {{shown}} von {{total}} Sperrgründen werden angezeigt. Bereiten Sie die Vorschau erneut vor, falls der vollständige Bericht nicht mehr verfügbar ist.',
    downloadReport: 'Fehlerbericht herunterladen',
  },
  fr: {
    title: 'Images et historique orgaMAX',
    description:
      'Reprenez les images et anciens documents préparés après l’import du tableau. Sélectionnez le dossier extrait et vérifiez l’aperçu avant de confirmer.',
    directory: 'Dossier du paquet extrait',
    folderHint:
      'Choisissez le dossier complet contenant manifest.json, records.jsonl et files. Extrayez d’abord les fichiers ZIP. Les originaux sont vérifiés localement avant le transfert.',
    targetTitle: 'Destination de la reprise',
    environment: 'Environnement',
    tenant: 'Locataire',
    origin: 'Adresse ERP',
    confirmTarget: 'J’ai vérifié ce locataire et cet environnement comme destination.',
    prepare: 'Préparer l’aperçu',
    confirm: 'Confirmer la reprise',
    resume: 'Reprendre cette importation',
    stop: 'Arrêter le transfert',
    retry: 'Réessayer',
    permissions: 'Vous devez pouvoir lire et gérer les imports.',
    targetError: 'La destination n’a pas pu être vérifiée. Rechargez-la avant de poursuivre.',
    statusError: 'L’état de l’exécution n’a pas pu être chargé. Rechargez-le avant de poursuivre.',
    localReady: 'Paquet vérifié localement',
    packageSummary: '{{records}} lignes source · {{files}} fichiers originaux',
    sourceSummary: 'Installation source : {{instance}} · Locataire source : {{tenant}}',
    restoreHint:
      'L’exécution enregistrée {{id}} est disponible. Sélectionnez le même dossier pour poursuivre avec son aperçu vérifié.',
    dmsPermission: 'Le transfert des originaux nécessite le droit de téléverser des documents.',
    executePermission: 'La confirmation nécessite le droit d’exécuter des imports.',
    previewTitle: 'Aperçu de la reprise',
    previewSummary: '{{created}} nouveaux · {{skipped}} déjà présents · {{invalid}} bloqués',
    previewBlocked:
      'Résolvez tous les blocages avant de confirmer. Aucune donnée métier n’a encore été importée.',
    previewReady:
      'L’aperçu ne présente aucun blocage. Confirmez explicitement pour démarrer la reprise.',
    jobProgress: 'Exécution {{id}} · {{status}} · {{count}} / {{total}} lignes',
    deferred:
      'L’historique et les originaux sont préservés. Les stocks et créances d’ouverture restent différés.',
    disconnectHint:
      'Fermer la page ou arrêter interrompt le transfert du navigateur. Les preuves enregistrées sont conservées ; sélectionnez le même dossier pour poursuivre. Une reprise confirmée continue sur le serveur.',
    environments: {
      development: 'Développement',
      test: 'Test',
      staging: 'Préproduction',
      production: 'Production',
    },
    phases: {
      VERIFY_RECORDS: 'Vérification des lignes source : {{current}} / {{total}}',
      VERIFY_FILES: 'Vérification des originaux : {{current}} / {{total}}',
      UPLOAD_RECORDS: 'Transfert des lignes source : {{current}} / {{total}}',
      UPLOAD_FILES: 'Transfert des originaux : {{current}} / {{total}}',
      PREVIEW: 'Préparation de l’aperçu sur le serveur…',
    },
    errors: {
      INVALID_PACKAGE: 'Le dossier ne contient pas un paquet complet pris en charge.',
      RECORD_INTEGRITY:
        'Des lignes source manquent, ont changé ou sont trop volumineuses. Sélectionnez le paquet complet inchangé.',
      FILE_INTEGRITY: 'Un original manque, a changé ou dépasse la taille autorisée.',
      SOURCE_MISMATCH:
        'L’installation source, le locataire ou la correspondance ne correspond pas au paquet.',
      CANCELLED:
        'Transfert arrêté. Sélectionnez le même paquet et préparez-le à nouveau pour poursuivre la même exécution.',
      TARGET_CHANGED: 'La destination a changé. Rechargez-la et vérifiez-la avant de poursuivre.',
      PERSISTENCE_UNAVAILABLE:
        'Le navigateur ne peut pas enregistrer la preuve de reprise. Activez le stockage local et réessayez.',
      TRANSFER_FAILED:
        'L’opération a été interrompue. Vérifiez la connexion et poursuivez avec le même paquet.',
    },
    confirmed: 'Reprise confirmée.',
    issuesLimited:
      'Les {{shown}} premiers blocages sur {{total}} sont affichés. Préparez à nouveau l’aperçu si le rapport complet n’est plus disponible.',
    downloadReport: 'Télécharger le rapport des blocages',
  },
  es: {
    title: 'Imágenes e historial de orgaMAX',
    description:
      'Importe las imágenes y los documentos históricos preparados después de importar la hoja de cálculo. Seleccione la carpeta extraída y revise la vista previa antes de confirmar.',
    directory: 'Carpeta del paquete extraído',
    folderHint:
      'Seleccione la carpeta completa con manifest.json, records.jsonl y files. Extraiga primero los archivos ZIP. Los originales se verifican localmente antes de transferirse.',
    targetTitle: 'Destino de la importación',
    environment: 'Entorno',
    tenant: 'Organización',
    origin: 'Dirección del ERP',
    confirmTarget: 'He verificado esta organización y este entorno como destino.',
    prepare: 'Preparar vista previa',
    confirm: 'Confirmar importación',
    resume: 'Reanudar esta importación',
    stop: 'Detener transferencia',
    retry: 'Reintentar',
    permissions: 'Necesita permiso para leer y administrar importaciones.',
    targetError: 'No se pudo verificar el destino. Vuelva a cargarlo antes de continuar.',
    statusError: 'No se pudo cargar el estado. Vuelva a cargarlo antes de continuar.',
    localReady: 'Paquete verificado localmente',
    packageSummary: '{{records}} filas de origen · {{files}} archivos originales',
    sourceSummary: 'Instalación de origen: {{instance}} · Organización de origen: {{tenant}}',
    restoreHint:
      'La ejecución guardada {{id}} está disponible. Seleccione la misma carpeta para continuar con su vista previa verificada.',
    dmsPermission: 'Necesita permiso para subir documentos para transferir los originales.',
    executePermission: 'Necesita permiso para ejecutar importaciones antes de confirmar.',
    previewTitle: 'Vista previa de importación',
    previewSummary: '{{created}} nuevos · {{skipped}} ya existentes · {{invalid}} bloqueados',
    previewBlocked:
      'Resuelva todos los bloqueos antes de confirmar. Todavía no se han importado datos de negocio.',
    previewReady:
      'La vista previa no tiene bloqueos. Confirme expresamente para iniciar la importación.',
    jobProgress: 'Ejecución {{id}} · {{status}} · {{count}} / {{total}} filas',
    deferred:
      'Se conservan el historial y los originales. Las existencias y cuentas por cobrar iniciales siguen aplazadas.',
    disconnectHint:
      'Cerrar la página o detener interrumpe la transferencia del navegador. Las pruebas guardadas se conservan; seleccione la misma carpeta para continuar. Una importación confirmada continúa en el servidor.',
    environments: {
      development: 'Desarrollo',
      test: 'Prueba',
      staging: 'Preproducción',
      production: 'Producción',
    },
    phases: {
      VERIFY_RECORDS: 'Verificando filas de origen: {{current}} / {{total}}',
      VERIFY_FILES: 'Verificando originales: {{current}} / {{total}}',
      UPLOAD_RECORDS: 'Transfiriendo filas de origen: {{current}} / {{total}}',
      UPLOAD_FILES: 'Transfiriendo originales: {{current}} / {{total}}',
      PREVIEW: 'Preparando la vista previa en el servidor…',
    },
    errors: {
      INVALID_PACKAGE: 'La carpeta no contiene un paquete completo compatible.',
      RECORD_INTEGRITY:
        'Faltan filas de origen, se han modificado o son demasiado grandes. Seleccione el paquete completo sin cambios.',
      FILE_INTEGRITY: 'Falta un original, se ha modificado o supera el tamaño permitido.',
      SOURCE_MISMATCH:
        'La instalación de origen, la organización o la asignación no coincide con el paquete.',
      CANCELLED:
        'Transferencia detenida. Seleccione el mismo paquete y prepárelo de nuevo para continuar la misma ejecución.',
      TARGET_CHANGED: 'El destino ha cambiado. Vuelva a cargarlo y verifíquelo antes de continuar.',
      PERSISTENCE_UNAVAILABLE:
        'El navegador no puede guardar la prueba para reanudar. Active el almacenamiento local y vuelva a intentarlo.',
      TRANSFER_FAILED:
        'Se interrumpió la operación. Compruebe la conexión y continúe con el mismo paquete.',
    },
    confirmed: 'Importación confirmada.',
    issuesLimited:
      'Se muestran los primeros {{shown}} de {{total}} bloqueos. Prepare de nuevo la vista previa si el informe completo ya no está disponible.',
    downloadReport: 'Descargar informe de incidencias',
  },
  tr: {
    title: 'orgaMAX görselleri ve geçmişi',
    description:
      'Hazırlanan görselleri ve geçmiş belgeleri tablo içe aktarımından sonra aktarın. Çıkarılan paket klasörünü seçin ve onaylamadan önce önizlemeyi inceleyin.',
    directory: 'Çıkarılmış paket klasörü',
    folderHint:
      'manifest.json, records.jsonl ve files içeren tam klasörü seçin. Önce ZIP dosyalarını çıkarın. Orijinaller aktarılmadan önce yerel olarak doğrulanır.',
    targetTitle: 'Aktarım hedefi',
    environment: 'Ortam',
    tenant: 'Kiracı',
    origin: 'ERP adresi',
    confirmTarget: 'Bu kiracıyı ve ortamı hedef olarak kontrol ettim.',
    prepare: 'Önizlemeyi hazırla',
    confirm: 'Aktarımı onayla',
    resume: 'Bu aktarımı sürdür',
    stop: 'Aktarımı durdur',
    retry: 'Yeniden dene',
    permissions: 'İçe aktarımları okuma ve yönetme izniniz olmalıdır.',
    targetError: 'Hedef doğrulanamadı. Devam etmeden önce yeniden yükleyin.',
    statusError: 'Çalışma durumu yüklenemedi. Devam etmeden önce durumu yeniden yükleyin.',
    localReady: 'Paket yerel olarak doğrulandı',
    packageSummary: '{{records}} kaynak satırı · {{files}} orijinal dosya',
    sourceSummary: 'Kaynak kurulumu: {{instance}} · Kaynak kiracı: {{tenant}}',
    restoreHint:
      'Kaydedilen {{id}} çalışması kullanılabilir. Doğrulanmış önizlemesiyle devam etmek için aynı paket klasörünü seçin.',
    dmsPermission: 'Orijinalleri aktarmak için belge yükleme izniniz olmalıdır.',
    executePermission: 'Onaylamak için içe aktarımları yürütme izniniz olmalıdır.',
    previewTitle: 'Aktarım önizlemesi',
    previewSummary: '{{created}} yeni · {{skipped}} zaten mevcut · {{invalid}} engellendi',
    previewBlocked: 'Onaylamadan önce tüm engelleri çözün. Henüz işletme verisi aktarılmadı.',
    previewReady: 'Önizlemede engel yok. Aktarımı başlatmak için açıkça onaylayın.',
    jobProgress: 'Çalışma {{id}} · {{status}} · {{count}} / {{total}} satır',
    deferred:
      'Geçmiş ve orijinaller korunur. Açılış stokları ve alacakları ertelenmeye devam eder.',
    disconnectHint:
      'Sayfayı kapatmak veya durdurmak tarayıcı aktarımını keser. Kaydedilmiş kanıtlar korunur; devam etmek için aynı klasörü seçin. Onaylanmış aktarım sunucuda devam eder.',
    environments: {
      development: 'Geliştirme',
      test: 'Test',
      staging: 'Hazırlık',
      production: 'Üretim',
    },
    phases: {
      VERIFY_RECORDS: 'Kaynak satırları denetleniyor: {{current}} / {{total}}',
      VERIFY_FILES: 'Orijinaller denetleniyor: {{current}} / {{total}}',
      UPLOAD_RECORDS: 'Kaynak satırları aktarılıyor: {{current}} / {{total}}',
      UPLOAD_FILES: 'Orijinaller aktarılıyor: {{current}} / {{total}}',
      PREVIEW: 'Sunucu önizlemesi hazırlanıyor…',
    },
    errors: {
      INVALID_PACKAGE: 'Klasör desteklenen eksiksiz bir paket içermiyor.',
      RECORD_INTEGRITY:
        'Kaynak satırları eksik, değiştirilmiş veya çok büyük. Değiştirilmemiş tam paketi seçin.',
      FILE_INTEGRITY: 'Bir orijinal dosya eksik, değiştirilmiş veya izin verilen boyutu aşıyor.',
      SOURCE_MISMATCH: 'Kaynak kurulumu, kiracı veya eşleme paketle eşleşmiyor.',
      CANCELLED:
        'Aktarım durduruldu. Aynı çalışmayı sürdürmek için aynı paketi seçin ve yeniden hazırlayın.',
      TARGET_CHANGED: 'Hedef değişti. Devam etmeden önce hedefi yeniden yükleyin ve doğrulayın.',
      PERSISTENCE_UNAVAILABLE:
        'Tarayıcı sürdürme kanıtını kaydedemiyor. Yerel depolamayı etkinleştirin ve yeniden deneyin.',
      TRANSFER_FAILED: 'İşlem kesildi. Bağlantınızı kontrol edin ve aynı paketle devam edin.',
    },
    confirmed: 'Aktarım onaylandı.',
    issuesLimited:
      '{{total}} engelin ilk {{shown}} tanesi gösteriliyor. Tam rapor artık yoksa önizlemeyi yeniden hazırlayın.',
    downloadReport: 'Sorun raporunu indir',
  },
};
